import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import {
  useGetAllStaffQuery,
  useGetMovementRegisterQuery,
} from "../../../store/appApi";
import { selectUser, selectUserRole } from "../../../store/authSlice";
import {
  createMovementEntryApi,
  updateMovementHoursApi,
} from "../../../services/movementRegister";

const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";
const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500";

// Fixed dropdown — half-hour steps, 0.5h to 12h. No free typing: keeps every
// entry's hours a clean, comparable value.
const HOURS_OPTIONS = Array.from({ length: 24 }, (_, i) => (i + 1) * 0.5);
const hoursLabel = (h) => `${h} hr${h === 1 ? "" : "s"}`;

const todayLabel = () =>
  new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const fmtDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const errMsg = (e) =>
  e?.response?.data?.message || "Something went wrong.";

const MovementRegister = () => {
  const currentUser = useSelector(selectUser);
  const userRole = useSelector(selectUserRole);
  // Only super-admin may browse everyone's register — everyone else,
  // including Project Manager, only ever sees their own entries and never
  // gets the employee-picker dropdown.
  const canSeeOthers = userRole === "super-admin";

  const { data: staffData } = useGetAllStaffQuery(
    { page: 1, limit: 500 },
    { skip: !canSeeOthers },
  );
  const employees = (staffData?.staff || []).filter(
    (item) => item?.status !== false,
  );

  // "" = my own entries. Only super-admin / Project Manager can pick someone
  // else — the form itself always logs for whoever is logged in.
  const [viewEmployeeId, setViewEmployeeId] = useState("");

  const [jobDescription, setJobDescription] = useState("");
  const [hours, setHours] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editHours, setEditHours] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  const {
    data: registerData,
    isLoading,
    isFetching,
    refetch,
  } = useGetMovementRegisterQuery({
    employeeId: canSeeOthers ? viewEmployeeId : "",
    limit: 100,
  });
  const entries = useMemo(
    () => registerData?.entries || [],
    [registerData],
  );

  const viewingSelf = !canSeeOthers || !viewEmployeeId;

  const submit = async (e) => {
    e.preventDefault();
    if (!jobDescription.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Job description required",
        text: "Describe the work that was done.",
      });
      return;
    }
    if (!hours) {
      Swal.fire({
        icon: "warning",
        title: "Select hours",
        text: "Pick how many hours this took from the dropdown.",
      });
      return;
    }
    try {
      setSaving(true);
      await createMovementEntryApi({
        jobDescription: jobDescription.trim(),
        hours: Number(hours),
      });
      setJobDescription("");
      setHours("");
      refetch();
      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Entry added to today's movement register.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Couldn't save",
        text: errMsg(err),
      });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry._id);
    setEditHours(String(entry.hours));
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditHours("");
  };
  const saveEdit = async (entry) => {
    if (!editHours) return;
    try {
      setUpdatingId(entry._id);
      await updateMovementHoursApi(entry._id, Number(editHours));
      cancelEdit();
      refetch();
      Swal.fire({
        icon: "success",
        title: "Updated",
        text: "Hours updated for this entry.",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Couldn't update",
        text: errMsg(err),
      });
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Movement Register" motherMenu="Workforce" />

      <div className="space-y-6">
        <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-xl shadow-slate-200/70">
          <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
            {todayLabel()}
          </div>
          <h1 className="mt-4 text-2xl font-semibold text-gray-900">
            Log work done outside your job profile
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Every entry is logged for <strong>{currentUser?.name || "you"}</strong>{" "}
            and dated to today — there's no back-dating. Once the work is
            finished, come back and correct the hours to what it actually took.
          </p>

          <form
            onSubmit={submit}
            className="mt-5 grid gap-4 sm:grid-cols-[1fr_180px_auto] sm:items-end"
          >
            <div>
              <label className={labelClass}>Job Description</label>
              <input
                className={inputClass}
                autoComplete="off"
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="What work did you do?"
              />
            </div>
            <div>
              <label className={labelClass}>Hours</label>
              <select
                className={inputClass}
                autoComplete="off"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
              >
                <option value="">Select hours</option>
                {HOURS_OPTIONS.map((h) => (
                  <option key={h} value={h}>
                    {hoursLabel(h)}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-amber-500 px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </form>
        </section>

        <section className="rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {viewingSelf ? "My Movement Register" : "Movement Register"}
              </h2>
              <p className="text-sm text-gray-500">
                {viewingSelf
                  ? "Every entry you've logged, most recent first."
                  : "Entries logged by the selected employee."}
              </p>
            </div>
            {canSeeOthers && (
              <select
                className={`${inputClass} w-64`}
                autoComplete="off"
                value={viewEmployeeId}
                onChange={(e) => setViewEmployeeId(e.target.value)}
              >
                <option value="">My own entries</option>
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.name} — {emp.role}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-5 py-2.5 text-left font-medium">Date</th>
                  {!viewingSelf && (
                    <th className="px-5 py-2.5 text-left font-medium">
                      Employee
                    </th>
                  )}
                  <th className="px-5 py-2.5 text-left font-medium">
                    Job Description
                  </th>
                  <th className="px-5 py-2.5 text-left font-medium">Hours</th>
                  <th className="px-5 py-2.5 text-left font-medium">Logged</th>
                  <th className="px-5 py-2.5 text-left font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {(isLoading || isFetching) && (
                  <tr>
                    <td
                      colSpan={viewingSelf ? 5 : 6}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      Loading…
                    </td>
                  </tr>
                )}
                {!isLoading && !isFetching && entries.length === 0 && (
                  <tr>
                    <td
                      colSpan={viewingSelf ? 5 : 6}
                      className="px-5 py-10 text-center text-gray-500"
                    >
                      No entries yet.
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  !isFetching &&
                  entries.map((entry) => {
                    const isEditing = editingId === entry._id;
                    const canEdit =
                      viewingSelf || userRole === "super-admin";
                    return (
                      <tr key={entry._id}>
                        <td className="px-5 py-2.5 text-gray-600">
                          {fmtDateTime(entry.date)}
                        </td>
                        {!viewingSelf && (
                          <td className="px-5 py-2.5 text-gray-600">
                            {entry.employeeName || "—"}
                          </td>
                        )}
                        <td className="px-5 py-2.5 text-gray-900">
                          {entry.jobDescription}
                        </td>
                        <td className="px-5 py-2.5 text-gray-600">
                          {isEditing ? (
                            <select
                              className={`${inputClass} w-28`}
                              autoComplete="off"
                              value={editHours}
                              onChange={(e) => setEditHours(e.target.value)}
                            >
                              {HOURS_OPTIONS.map((h) => (
                                <option key={h} value={h}>
                                  {hoursLabel(h)}
                                </option>
                              ))}
                            </select>
                          ) : (
                            hoursLabel(entry.hours)
                          )}
                        </td>
                        <td className="px-5 py-2.5 text-gray-500">
                          {fmtDateTime(entry.createdAt)}
                        </td>
                        <td className="px-5 py-2.5 text-right">
                          {canEdit &&
                            (isEditing ? (
                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-slate-950 transition hover:bg-amber-400 disabled:opacity-60"
                                  disabled={updatingId === entry._id}
                                  onClick={() => saveEdit(entry)}
                                >
                                  {updatingId === entry._id ? "…" : "Save"}
                                </button>
                                <button
                                  type="button"
                                  className="rounded-lg border-2 border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:border-gray-400"
                                  onClick={cancelEdit}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="rounded-lg border-2 border-amber-500 px-3 py-1.5 text-xs font-semibold text-amber-700 transition hover:bg-amber-50"
                                onClick={() => startEdit(entry)}
                              >
                                Update hours
                              </button>
                            ))}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
};

export default MovementRegister;
